import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UpsertCouponWithPromoCodeDto } from 'views/Coupons/types';
import { buildQueryParams } from 'utils/queryParamsUtil';

import {
  Coupon,
  CouponConfiguration,
  GetCouponsPaginatedResponse
} from '../common/contracts';
import { EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import {
  ApiRoutes,
  EApiRoutes,
  fetchConfig,
  getApiRouteOrMock
} from './api.utils';

export interface CouponsFilters {
  page?: number;
  limit?: number;
  isActive?: boolean;
}

export const DEFAULT_COUPONS_PAGE_SIZE = 50;

export const COUPONS_FILTER_OPTIONS_LIMIT = 500;

export const DEFAULT_COUPONS_FILTERS: CouponsFilters = {
  page: 1,
  limit: DEFAULT_COUPONS_PAGE_SIZE
};

export const getCouponsIsActiveFilter = (
  statusValues: string[]
): boolean | undefined => {
  const hasActiveStatus = statusValues.includes('active');
  const hasInactiveStatus = statusValues.includes('inactive');

  if (hasActiveStatus && !hasInactiveStatus) {
    return true;
  }
  if (hasInactiveStatus && !hasActiveStatus) {
    return false;
  }
  return undefined;
};

export type UseCouponsOptions = {
  publisherId?: string | null;
  filters?: CouponsFilters;
  couponId?: string | null;
};

export default function useCoupons({
  publisherId,
  filters = {},
  couponId
}: UseCouponsOptions = {}) {
  const mergedFilters = { ...DEFAULT_COUPONS_FILTERS, ...filters };
  const axios = useAxios();
  const queryClient = useQueryClient();

  const getCoupons = useQuery<GetCouponsPaginatedResponse>({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.COUPONS, publisherId, mergedFilters],
    queryFn: async () => {
      const coupons = await axios.get(
        `${getApiRouteOrMock(EApiRoutes.COUPONS_PAGINATED)}?${buildQueryParams(mergedFilters)}`,
        {},
        publisherId ? { 'x-publishers': publisherId } : undefined
      );
      return coupons as GetCouponsPaginatedResponse;
    },
    enabled: !!publisherId
  });

  const getCouponsConfiguration = useQuery({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.COUPONS_CONFIGURATION, publisherId],
    queryFn: async () => {
      const coupons = await axios.get(
        getApiRouteOrMock(EApiRoutes.COUPONS_CONFIGURATION),
        {},
        publisherId ? { 'x-publishers': publisherId } : undefined
      );
      return coupons as CouponConfiguration;
    },
    enabled: !!publisherId
  });

  const getSingleCoupon = useQuery({
    queryKey: [EQueryKeys.SINGLE_COUPON, couponId],
    queryFn: async () => {
      const coupon = await axios.get(
        `${ApiRoutes.COUPONS}/${couponId}`,
        {},
        publisherId ? { 'x-publishers': publisherId } : undefined
      );
      return coupon as Coupon;
    },
    enabled: !!couponId && !!publisherId
  } as const);

  const updateCouponsConfiguration = useMutation({
    mutationFn: async (couponsConfiguration: CouponConfiguration) => {
      const response = await axios.put(
        getApiRouteOrMock(EApiRoutes.COUPONS_CONFIGURATION),
        couponsConfiguration,
        publisherId ? { 'x-publishers': publisherId } : undefined
      );
      return response.data;
    },
    onSuccess: async () =>
      queryClient.invalidateQueries({
        queryKey: [EQueryKeys.COUPONS_CONFIGURATION, publisherId]
      })
  });

  const setCouponActive = useMutation(
    async ({ couponId, active }: { couponId: string; active: boolean }) => {
      const response = await axios.put(
        `${ApiRoutes.COUPONS}/${couponId}/setActivation`,
        { active },
        publisherId ? { 'x-publishers': publisherId } : undefined
      );
      return response.data;
    }
  );

  const upsertCoupon = useMutation(
    async (upsertCouponData: UpsertCouponWithPromoCodeDto) => {
      const response = await axios.post(
        getApiRouteOrMock(EApiRoutes.COUPONS),
        upsertCouponData,
        publisherId ? { 'x-publishers': publisherId } : undefined
      );
      return response.data;
    }
  );

  const isPromoCodeActive = useMutation(async (promoCodeName: string) => {
    return await axios.get(
      `${getApiRouteOrMock(EApiRoutes.PROMO_CODE)}/${promoCodeName}/isActive`,
      {},
      publisherId ? { 'x-publishers': publisherId } : undefined
    );
  });

  return {
    getCoupons,
    getSingleCoupon,
    upsertCoupon,
    setCouponActive,
    isPromoCodeActive,
    updateCouponsConfiguration,
    getCouponsConfiguration
  };
}
